import React, { createContext, useContext, useState } from 'react';
import { useAuth } from './AuthContext';
import { useClient } from './ClientContext';
import AgentClientChat from '../components/AgentClientChat';
import { AgentClientChatContextType } from '../types/chat';
import { useAgentClientChat as useAgentClientChatHook } from '../hooks/useAgentClientChat';

const AgentClientChatContext = createContext<AgentClientChatContextType | undefined>(undefined);

export function AgentClientChatProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { hasAgent } = useClient();
  
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);

  // Use the hook with auto-connect disabled by default to prevent unnecessary connections
  const {
    messages,
    isLoading,
    isConnected,
    unreadCount,
    connectionError,
    sendMessage,
    markAsRead,
    refreshMessages,
    connect,
    disconnect
  } = useAgentClientChatHook({ autoConnect: false });

  const openChat = () => {
    setIsChatOpen(true);
    setIsChatMinimized(false);
    // Only connect if we're not already connected or connecting
    if (!isConnected && !isLoading) {
      console.log('Opening chat - connecting WebSocket');
      connect();
    } else {
      console.log('Opening chat - WebSocket already connected or connecting');
    }
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setIsChatMinimized(false);
    // Disconnect the WebSocket when chat is closed
    console.log('Closing chat - disconnecting WebSocket');
    disconnect();
  };

  const minimizeChat = () => {
    setIsChatMinimized(true);
  };

  const maximizeChat = () => {
    setIsChatMinimized(false);
  };

  return (
    <AgentClientChatContext.Provider
      value={{
        messages,
        isLoading,
        isConnected,
        unreadCount,
        connectionError,
        sendMessage,
        markAsRead,
        refreshMessages,
        isChatOpen,
        openChat,
        closeChat,
        isChatMinimized,
        minimizeChat,
        maximizeChat,
        connect,
        disconnect
      }}
    >
      {children}
      {/* Only render chat if user has agent and is authenticated */}
      {hasAgent && isAuthenticated && (
        <AgentClientChat 
          open={isChatOpen}
          onClose={closeChat}
        />
      )}
    </AgentClientChatContext.Provider>
  );
}

export const useAgentClientChat = () => {
  const context = useContext(AgentClientChatContext);
  if (context === undefined) {
    throw new Error('useAgentClientChat must be used within an AgentClientChatProvider');
  }
  return context;
};